import { useEffect, useRef, useState, type ReactNode } from "react";
import { isReducedActive } from "@/lib/motionPreference";
import { isReadingMode } from "@/lib/readingMode";

interface RevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  /** Animation duration in ms (default 800) */
  duration?: number;
  as?: "div" | "section" | "article" | "aside" | "figure" | "li";
}

/**
 * Subtle editorial scroll-reveal: fades up ~20px, slow & luxurious.
 * Respects prefers-reduced-motion.
 */
const Reveal = ({
  children,
  className = "",
  delay = 0,
  duration = 800,
  as: Tag = "div",
}: RevealProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isReducedActive() || isReadingMode()) {
      setShown(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
